import { useState, useEffect } from "react";
import { Mail, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@donex/ui";
import { Input } from "@donex/ui";
import { Label } from "@donex/ui";
import { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator } from "@donex/ui";
import { supabase } from "@/lib/supabase";
import logo from "@/assets/donex-logo.svg";

type Step = "email" | "otp";

export default function AuthPage() {
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const prefilledEmail = params.get("email");
    if (prefilledEmail) {
      setEmail(prefilledEmail);
      setStep("otp");
      toast.success("Code sent", { description: `Check ${prefilledEmail} for your sign-in code.` });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const sendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    const { error } = await supabase.auth.signInWithOtp({ email: email.trim() });
    setLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    setStep("otp");
    toast.success("Code sent", { description: `Check ${email} for your sign-in code.` });
  };

  const verifyOtp = async () => {
    if (otp.length !== 6) return;
    setLoading(true);
    const { error } = await supabase.auth.verifyOtp({
      email: email.trim(),
      token: otp,
      type: "email",
    });
    setLoading(false);
    if (error) {
      toast.error(error.message);
      setOtp("");
    }
    // on success, onAuthStateChange in AuthProvider picks up the new session
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-32 h-[480px] w-[480px] rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute top-1/3 -right-40 h-[420px] w-[420px] rounded-full bg-accent/5 blur-3xl" />
      </div>

      <div className="relative w-full max-w-sm">
        <div className="mb-8 flex justify-center">
          <img src={logo} alt="Done[x]" className="h-[1.3rem] w-auto" />
        </div>

        <div
          className="rounded-2xl border border-border bg-surface p-8"
          style={{ boxShadow: "var(--shadow-card)" }}
        >
          {step === "email" ? (
            <form onSubmit={sendOtp} className="space-y-5">
              <div className="space-y-1">
                <h1 className="text-lg font-semibold tracking-tight text-foreground">Sign in</h1>
                <p className="text-sm text-muted-foreground">
                  Enter your email to receive a one-time code.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Email
                </Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoFocus
                  required
                  disabled={loading}
                />
              </div>

              <Button
                type="submit"
                disabled={loading || !email.trim()}
                className="w-full text-primary-foreground"
                style={{ backgroundImage: "var(--gradient-primary)" }}
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Mail className="h-4 w-4" />
                )}
                {loading ? "Sending…" : "Send code"}
              </Button>
            </form>
          ) : (
            <div className="space-y-5">
              <div className="space-y-1">
                <h1 className="text-lg font-semibold tracking-tight text-foreground">
                  Check your email
                </h1>
                <p className="text-sm text-muted-foreground">
                  We sent a 6-digit code to{" "}
                  <span className="font-medium text-foreground">{email}</span>.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Code
                </Label>
                <InputOTP
                  maxLength={6}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={otp}
                  onChange={(v) => {
                    const digits = v.replace(/\D/g, "");
                    setOtp(digits);
                    if (digits.length === 6) {
                      setTimeout(verifyOtp, 0);
                    }
                  }}
                  disabled={loading}
                  containerClassName="w-full"
                >
                  <InputOTPGroup className="flex-1">
                    <InputOTPSlot index={0} className="flex-1 w-auto" />
                    <InputOTPSlot index={1} className="flex-1 w-auto" />
                    <InputOTPSlot index={2} className="flex-1 w-auto" />
                  </InputOTPGroup>
                  <InputOTPSeparator />
                  <InputOTPGroup className="flex-1">
                    <InputOTPSlot index={3} className="flex-1 w-auto" />
                    <InputOTPSlot index={4} className="flex-1 w-auto" />
                    <InputOTPSlot index={5} className="flex-1 w-auto" />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <Button
                onClick={verifyOtp}
                disabled={loading || otp.length !== 6}
                className="w-full text-primary-foreground"
                style={{ backgroundImage: "var(--gradient-primary)" }}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {loading ? "Verifying…" : "Verify code"}
              </Button>

              <button
                type="button"
                onClick={() => {
                  setStep("email");
                  setOtp("");
                }}
                className="flex w-full items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="h-3 w-3" /> Back to email
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
